import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, ReplaySubject } from 'rxjs';
import { FormHelper } from '@helpers/form.helper';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { EntityModel } from '@data-layer/models/entities';
import { EntrySummaryModel, InvoiceModel, LineModel } from '@data-layer/models/entry-summaries';
import {
  enumEntrySummaryDutiesPaymentType,
  enumEntrySummaryState,
  enumEntrySummarySubmissionType,
  enumEntrySummaryType,
  enumTransportationMode,
} from '@data-layer/enums/entry-summaries';
import { IErrorForm } from '@shared/form-wrapper/interfaces';
import { CUSTOM_MASK, CustomValidator, notTheSameAsValidator } from '@helpers/custom-validator';
import { isCbpAssignedNumberImporter } from '@helpers/importer.helper';
import { CompaniesService } from '@services/companies.service';
import {
  ForeignPortModel,
  LocationOfGoodsModel,
  PortModel,
  ShipmentModel,
  StateModel,
  ValidationMessageModel,
} from '@data-layer/models/common';
import { EsTransportationModeHelper } from '@helpers/es-transportation-mode.helper';
import { ActivatedRoute, Router } from '@angular/router';
import { EntrySummaryStateService, EsLabelsService } from '../../../services';
import { Location } from '@angular/common';
import { CodeRepository } from '@data-layer/repositories';
import { distinctUntilChanged, map, switchMap, takeUntil } from 'rxjs/operators';
import { DateHelper } from '@helpers/date.helper';
import { cloneDeep, flatten } from 'lodash-es';
import { UtilsHelper } from '@helpers/utils-helper';
import { MarineInsuranceModel } from '@data-layer/models/marine-insurance';
import { PaymentBillItemPendingModel } from '@data-layer/models/payments';
import { enumUserBetaFlag } from '@app/data-layer/enums/user';
import { EXPORT_DATE_LATER_THAN_ARRIVAL_DATE } from '@constants/entity-validation-messages';

@Component({
  selector: 'es-edit-shipment-desktop',
  template: `
    <p class="sidebar-form-description">{{ 'LABELS.EDIT_SHIPMENT_DETAILS_DESCRIPTION' | translate }}</p>
    <span class="sidebar-form-hint">{{ 'LABELS.EDIT_SHIPMENT_DETAILS_HINT' | translate }}</span>

    <form autocomplete="off" [formGroup]="formGroup" (ngSubmit)="onSubmit()">
      <ng-container *ngIf="isMainInfo">
        <div class="shipment-type" [ngClass]="{ 'shipment-type-with-truck': isTruckModeAvailable }">
          <div
            class="shipment-type-item"
            [ngClass]="{ 'is-active': isAir }"
            (click)="onTransportationModeSelect(enumTransportationMode.AIR)"
          >
            <div class="shipment-type-item-icon">
              <img src="assets/icons/ic_plain.svg" />
            </div>
            <div class="shipment-type-item-text">
              {{ 'LABELS.AIR' | translate }}
            </div>
          </div>

          <div
            class="shipment-type-item"
            [ngClass]="{ 'is-active': isOcean }"
            (click)="onTransportationModeSelect(enumTransportationMode.OCEAN)"
          >
            <div class="shipment-type-item-icon">
              <img src="assets/icons/vessel.svg" />
            </div>
            <div class="shipment-type-item-text">
              {{ 'LABELS.OCEAN' | translate }}
            </div>
          </div>

          <div
            class="shipment-type-item"
            [ngClass]="{ 'is-active': isRail }"
            (click)="onTransportationModeSelect(enumTransportationMode.RAIL)"
          >
            <div class="shipment-type-item-icon">
              <img src="assets/icons/rail.svg" />
            </div>
            <div class="shipment-type-item-text">
              {{ 'LABELS.RAIL' | translate }}
            </div>
          </div>

          <!-- #13261: Truck is a beta feature (es_trucker_integration) — the page decides via isTruckModeAvailable -->
          <div
            *ngIf="isTruckModeAvailable"
            class="shipment-type-item"
            [ngClass]="{ 'is-active': isTruck }"
            (click)="onTransportationModeSelect(enumTransportationMode.TRUCK)"
          >
            <div class="shipment-type-item-icon">
              <img src="assets/icons/icon_truck.svg" />
            </div>
            <div class="shipment-type-item-text">
              {{ 'TRUCK' | translate }}
            </div>
          </div>
        </div>

        <div class="shipment-details">
          <div class="form-row">
            <div *ngIf="isOcean" class="col-12">
              <app-control-wrapper [control]="formGroup.get('vesselName')" [errors]="errors.vesselName" [isShowHelper]="false">
                <label for="vesselName" control-label>{{ 'LABELS.VESSEL_NAME' | translate }}</label>
                <input
                  id="vesselName"
                  class="form-control"
                  type="text"
                  name="vesselName"
                  control-wrapper
                  [formControl]="formGroup.get('vesselName')"
                  [patterns]="CUSTOM_MASK"
                  [mask]="'F{20}'"
                  [maxMaskLength]="20"
                  [appMaskErrorTooltip]="'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
                  [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                  [dropSpecialCharacters]="false"
                />
              </app-control-wrapper>
            </div>
            <div *ngIf="!isTruck" class="col-12">
              <app-control-wrapper [control]="formGroup.get('trip')" [errors]="errors.trip" [isShowHelper]="false">
                <label for="trip" control-label>
                  {{ tripLabel | translate }}
                </label>
                <input
                  id="trip"
                  class="form-control"
                  type="text"
                  name="trip"
                  control-wrapper
                  [formControl]="formGroup.get('trip')"
                  [patterns]="CUSTOM_MASK"
                  [mask]="isAir ? '0{5}' : 'F{5}'"
                  [appMaskErrorTooltip]="isAir ? 'MASK_ERROR.ONLY_DIGITS' : 'MASK_ERROR.ONLY_ENGLISH_DIGITS_AND_SPECIAL'"
                  [maxLengthMessage]="isAir ? 'MASK_ERROR.MAX_DIGITS_LENGTH' : null"
                  [maxMaskLength]="5"
                  [placeholder]="'FORM_PLACEHOLDER.ENTER' | translate"
                  [dropSpecialCharacters]="false"
                />
              </app-control-wrapper>
            </div>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="isFromInfo">
        <div class="form-row">
          <div *ngIf="!isAir && !isTruck" class="col-12">
            <auto-select
              [label]="'FORM_LABEL.FOREIGN_PORT'"
              [bindLabel]="['code', 'portName', 'countryCode']"
              [bindOption]="['code', 'portName', 'countryCode']"
              [bindValue]="'id'"
              [id]="'foreignPortOfLadingId'"
              [change]="getForeignPorts()"
              [initializeItems]="initialForeignPorts"
              [fc]="formGroup.get('foreignPortOfLadingId')"
              [helper]="'FORM_HELPER.FOREIGN_PORT_OF_LADING'"
              [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
              [errors]="errors.foreignPortOfLadingId"
            ></auto-select>
          </div>
          <div class="col-12 mb-3">
            <date-picker
              [fc]="formGroup.get('exportDate')"
              [helper]="exportHelper"
              [errors]="errors.exportDate"
              [label]="'FORM_LABEL.EXPORT_DATE'"
              [id]="'exportDate'"
              [name]="'exportDate'"
              [maxDate]="exportMaxDate"
              [placeholder]="'FORM_PLACEHOLDER.CHOOSE_DATE'"
              [showCurrentDate]="true"
            >
              <span custom-error *ngIf="isFromInfo && exportDateErrorText" class="invalid-form-control">
                {{ exportDateErrorText | translate }}
              </span>
            </date-picker>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="isToInfo">
        <div class="form-row">
          <div class="col-12">
            <p class="text-hint">{{ 'ENTRY_SUMMARY.PLACE_OF_FINAL_DESTINATION' | translate }}</p>
          </div>

          <div class="col-12">
            <auto-select
              [label]="'FORM_LABEL.LOCATION_OF_GOODS'"
              [bindLabel]="['code', 'name']"
              [bindValue]="'code'"
              [id]="'locationCode'"
              [change]="searchLocations()"
              [initializeItems]="initialLocations"
              [updateCountCharacter]="3"
              [fc]="formGroup.get('locationCode')"
              (update)="updateLocation($event)"
              [helper]="locationHelper"
              [errors]="errors.locationCode"
            ></auto-select>
          </div>

          <div class="col-12">
            <auto-select
              [label]="'FORM_LABEL.PORT_OF_ENTRY'"
              [bindLabel]="['code', 'description']"
              [bindValue]="'code'"
              [id]="'portOfEntryCode'"
              [change]="getPorts()"
              [initializeItems]="initialPorts"
              [dropdownPosition]="'top'"
              [errors]="errors.portOfEntryCode"
              [fc]="formGroup.get('portOfEntryCode')"
              [helper]="'FORM_HELPER.PORT_OF_ENTRY'"
              [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
              [searchOnOpen]="true"
              [updateCountCharacter]="0"
            ></auto-select>
          </div>

          <div class="col-12">
            <auto-select
              [label]="'FORM_LABEL.STATE_OF_DESTINATION'"
              [bindLabel]="['code', 'description']"
              [bindOption]="['code', 'description']"
              [bindValue]="'code'"
              [change]="searchStates()"
              [id]="'stateOfDestination'"
              [dropdownPosition]="'top'"
              [initializeItems]="initialStates"
              [errors]="errors.stateOfDestination"
              [fc]="formGroup.get('stateOfDestination')"
              [isShowHelper]="isTruck"
              [helper]="stateHelper"
            ></auto-select>
          </div>

          <div class="col-12">
            <date-picker
              [fc]="formGroup.get('arrivalDate')"
              [helper]="arrivalDateHelperStr"
              [errors]="errors.arrivalDate"
              [label]="'LABELS.ARRIVAL_DATE'"
              [id]="'arrivalDate'"
              [name]="'arrivalDate'"
              [minDate]="arrivalMinDate"
              [placeholder]="'FORM_PLACEHOLDER.CHOOSE_DATE'"
              [showCurrentDate]="true"
            ></date-picker>
          </div>
        </div>
      </ng-container>

      <div class="form-row mt-3" *ngIf="isMainInfo || isViaInfo">
        <div class="col-12">
          <div class="custom-control custom-checkbox">
            <input
              type="checkbox"
              [formControl]="formGroup.get('differentPortEntry')"
              class="custom-control-input"
              id="differentPortEntry"
            />
            <label class="custom-control-label" for="differentPortEntry">{{ 'ENTRY_SUMMARY.VIA_DESC' | translate }}</label>
          </div>
        </div>
        <ng-container *ngIf="formGroup.get('differentPortEntry').value">
          <div class="col-12 mt-2">
            <auto-select
              [label]="'FORM_LABEL.PORT_OF_UNLADING_ARRIVAL'"
              [bindLabel]="['code', 'description']"
              [bindValue]="'code'"
              [change]="getPorts()"
              [id]="'portOfUnladingCode'"
              [initializeItems]="initialPorts"
              [errors]="errors.portOfUnladingCode"
              [fc]="formGroup.get('portOfUnladingCode')"
              [helper]="'FORM_HELPER.PORT_OF_UNLADING_ARRIVAL'"
              [placeholder]="'FORM_PLACEHOLDER.SEARCH_CODE_OR_NAME' | translate"
            ></auto-select>
          </div>
        </ng-container>
      </div>

      <div class="sidebar-form-buttons">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="onSubmit(false)"></missing-fields>
  `,
  styleUrls: ['./es-edit-shipment-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsEditShipmentDesktopComponent implements OnInit, OnDestroy {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public isMainInfo: boolean;
  @Input() public isFromInfo: boolean;
  @Input() public isToInfo: boolean;
  @Input() public isViaInfo: boolean;
  /** #13261: shows the Truck tile; set by the host page from the `es_trucker_integration` beta flag. */
  @Input() public isTruckModeAvailable: boolean = false;

  @Output() public dataSaved: EventEmitter<EntityModel> = new EventEmitter();

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  public formGroup: UntypedFormGroup;
  public errors: { [name: string]: IErrorForm };
  public initialForeignPorts: ForeignPortModel[] = [];
  public initialPorts: PortModel[] = [];
  public initialLocations: LocationOfGoodsModel[] = [];
  public initialStates: StateModel[] = [];
  public returnUrl: string;
  public CUSTOM_MASK = CUSTOM_MASK;
  public headerIconName = 'vessel';
  public payerUnitNumber: string;
  public achPaymentType: enumEntrySummaryDutiesPaymentType = enumEntrySummaryDutiesPaymentType.ACH;
  public showChangeEntrySubmissionTypeConfirmModal: boolean = false;
  public showChangeEntryTypeConfirmModal: boolean = false;
  public exportMaxDate: string;
  public arrivalMinDate: string;
  public arrivalDateHelper = new BehaviorSubject<string>('FORM_HELPER.SHIPMENT_ARRIVAL_DATE');
  public arrivalDateHelper$: Observable<string>;
  public types = [
    {
      value: enumEntrySummaryType.FORMAL,
      label: 'FORM_LABEL.FORMAL',
      helper: 'FORM_HELPER.FORMAL',
      tooltip: 'TOOLTIP.SHIPMENT_TYPE_TOOLTIP',
    },
    {
      value: enumEntrySummaryType.INFORMAL,
      label: 'FORM_LABEL.INFORMAL',
      helper: 'FORM_HELPER.INFORMAL',
      tooltip: 'TOOLTIP.SHIPMENT_TYPE_TOOLTIP',
    },
  ];

  public showUpdateCompanyAchDisclaimer: boolean = false;

  public enumTransportationMode = enumTransportationMode;
  public enumUserBetaFlag: typeof enumUserBetaFlag = enumUserBetaFlag;

  private isDestroy: ReplaySubject<boolean> = new ReplaySubject();

  constructor(
    private router: Router,
    private entrySummaryStateService: EntrySummaryStateService,
    private fb: UntypedFormBuilder,
    private activeRoute: ActivatedRoute,
    private fh: FormHelper,
    private location: Location,
    private codeRepository: CodeRepository,
    private companiesService: CompaniesService,
    private esLabelsService: EsLabelsService,
  ) {}

  public get isShowSubmissionType(): boolean {
    return this.formGroup.get('submissionType').enabled;
  }

  public get isAir(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.AIR;
  }

  public get isOcean(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.OCEAN;
  }

  public get isRail(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.RAIL;
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }

  public ngOnInit(): void {
    const company = this.companiesService.getCompany();
    this.returnUrl = this.activeRoute.snapshot.queryParams.returnUrl;
    this.payerUnitNumber = company.payerUnitNumber;

    this.formGroup = this.fh.createEntryShipmentFormGroup(company?.authorizationAgreementAccepted && !!company?.payerUnitNumber);
    this.checkAuthorizationAgreement();
    this.createErrors();
    this.initUpdatingDatesErrors();
    this.initValidatorChanges();
    this.arrivalDateHelper$ = this.arrivalDateHelper.asObservable();

    const isValidateAfterInit = this.activeRoute.snapshot.queryParams.validateAfterInit
      ? JSON.parse(this.activeRoute.snapshot.queryParams.validateAfterInit)
      : false;
    if (isValidateAfterInit) {
      this.fh.markAsTouched(this.formGroup);
    }

    this.entrySummaryStateService.entrySummary$.pipe(takeUntil(this.isDestroy)).subscribe((entrySummary) => {
      this.entrySummary = entrySummary;
      this.headerIconName = this.esLabelsService.getHeaderIconName(this.entrySummary?.transportationMode);
      this.updateEntrySummaryForm(isValidateAfterInit);
      this.updateTotalESEnteredValue();
      this.updateTransportationModeValidation();
    });
  }

  /**
   * #13261: the mode lives on the ES object (no form control) — mutate it and re-apply the
   * mode-dependent validators right away, so switching e.g. Air→Ocean makes Vessel required again.
   */
  public onTransportationModeSelect(transportationMode: enumTransportationMode): void {
    this.entrySummary.transportationMode = transportationMode;
    this.updateTransportationModeValidation();
  }

  public onSubmit(needValidate: boolean = true) {
    let fieldsToSkip = [];
    const allRequiredFormControlKeys = Object.keys(this.formGroup.controls);
    if (this.isFromInfo) {
      fieldsToSkip = allRequiredFormControlKeys.filter((item) => !['foreignPortOfLadingId', 'exportDate'].includes(item));
    } else if (this.isToInfo) {
      fieldsToSkip = allRequiredFormControlKeys.filter(
        (item) => !['locationCode', 'portOfEntryCode', 'stateOfDestination', 'arrivalDate'].includes(item),
      );
    } else if (this.isMainInfo) {
      fieldsToSkip = allRequiredFormControlKeys.filter((item) => !['vesselName', 'trip'].includes(item));
    } else if (this.isViaInfo) {
      fieldsToSkip = allRequiredFormControlKeys.filter((item) => !['portOfUnladingCode'].includes(item));
    }

    const invalidFields = this.getInvalidFields(fieldsToSkip);
    if (needValidate && Object.keys(invalidFields).length) {
      this.openModal(invalidFields);
      return;
    }

    this.createOrUpdateEntrySummary()
      .pipe(
        takeUntil(this.isDestroy),
        switchMap((data: EntrySummaryModel) => {
          if (
            this.entrySummary.submissionType === enumEntrySummarySubmissionType.MANUAL &&
            data.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED
          ) {
            return this.entrySummaryStateService.copyCompanyPoaToEntryIfNeeded(data).pipe(map(() => data));
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => {
          if (data.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.ACH) {
            return this.entrySummaryStateService.copyCompanyPunToEntryIfNeeded(data).pipe(map(() => data));
          }
          return of(data);
        }),
        switchMap((data: EntrySummaryModel) => this.entrySummaryStateService.updateDocuments(true).pipe(map(() => data))),
        switchMap((data: EntrySummaryModel) => {
          return this.createOrUpdateShipment(data);
        }),
        switchMap((data: EntrySummaryModel) => this.clearHouseBillIfHidden(data)),
        switchMap(() => this.entrySummaryStateService.loadEntrySummary(this.entrySummary.referenceNumber)),
        switchMap((data: EntrySummaryModel) => combineLatest([of(data), this.entrySummaryStateService.updateInsuranceShipmentDate(data)])),
      )
      .subscribe(([data, _]: [EntrySummaryModel, [MarineInsuranceModel, PaymentBillItemPendingModel]]) => {
        // #12298: edits are persisted — clear dirty so future server refreshes apply again.
        this.formGroup.markAsPristine();
        this.entrySummaryStateService.updateCurrentEntrySummary(data);
        this.dataSaved.emit();
      });
  }

  public getPorts() {
    return this.searchPlaceOfDelivery.bind(this);
  }

  public searchStates() {
    return this.codeRepository.searchState.bind(this.codeRepository, 'US');
  }

  public getForeignPorts() {
    return this.codeRepository.searchForeignPort.bind(this.codeRepository);
  }

  public searchLocations() {
    return this.codeRepository.searchLocations.bind(this.codeRepository);
  }

  public decline(): void {
    this.onSubmit(false);
  }

  public updateLocation(location: LocationOfGoodsModel) {
    if (location && location.portCode) {
      this.formGroup.get('portOfEntryCode').patchValue(location.portCode);
      this.formGroup.get('stateOfDestination').patchValue(location.stateCode);
      this.getCurrentPorts(location.portCode);
      this.getCurrentState(location.stateCode);
    }
  }

  public ngOnDestroy(): void {
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  public get exportDateErrorText(): string {
    let error: string = null;
    if (this.findShipmentError(EXPORT_DATE_LATER_THAN_ARRIVAL_DATE)) {
      error = EXPORT_DATE_LATER_THAN_ARRIVAL_DATE;
    }

    return error ? `BACK_ERROR.${error}` : null;
  }

  public get tripLabel(): string {
    switch (this.entrySummary?.transportationMode) {
      case enumTransportationMode.AIR:
        return 'FORM_LABEL.FLIGHT_NUMBER';
      case enumTransportationMode.OCEAN:
        return 'LABELS.VOYAGE_NUMBER';
      case enumTransportationMode.RAIL:
        return 'FORM_LABEL.RAIL_TRIP_NUMBER';
      default:
        break;
    }
  }

  public get exportHelper(): string {
    if (this.isAir) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_AIR';
    } else if (this.isOcean || this.isRail) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_SHIP_DESKTOP';
    } else if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_EXPORT_DATE_TRUCK';
    }
  }

  public get stateHelper(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_STATE_TRUCK';
    } else {
      return '';
    }
  }

  public get locationHelper(): string {
    return this.isTruck ? 'FORM_HELPER.LOCATION_OF_GOODS_TRUCK' : 'FORM_HELPER.LOCATION_OF_GOODS';
  }

  public get arrivalDateHelperStr(): string {
    if (this.isTruck) {
      return 'FORM_HELPER.SHIPMENT_ARRIVAL_DATE_TRUCK';
    } else {
      return this.arrivalDateHelper.getValue();
    }
  }

  /** Mode-dependent validators (#13261) — shared with the OCR quick-fix step via FormHelper (#13422). */
  private updateTransportationModeValidation(): void {
    this.fh.applyEntryShipmentModeValidators(this.formGroup, this.entrySummary?.transportationMode);
  }

  private searchPlaceOfDelivery(term: string) {
    return this.formGroup.get('submissionType').value === enumEntrySummarySubmissionType.BROKER_ASSISTED
      ? this.codeRepository.searchDomesticPorts(term)
      : this.entrySummaryStateService.searchPlaceOfDelivery(term);
  }

  private updateTotalESEnteredValue() {
    if (this.entrySummary && this.entrySummary.referenceNumber) {
      this.formGroup.get('type').clearValidators();
      this.entrySummaryStateService
        .getEntrySummaryEnteredValue(this.entrySummary)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((totalEnteredValue: number) => {
          this.formGroup.get('type').setValidators(
            totalEnteredValue !== 0
              ? [
                  Validators.required,
                  CustomValidator.shipmentTypeEnteredValue(
                    totalEnteredValue,
                    this.getAllLines().map((line) => line?.countryOfOrigin?.code),
                    isCbpAssignedNumberImporter(this.entrySummary.importer),
                  ),
                ]
              : Validators.required,
          );
        });
    }
  }

  private getAllLines(): LineModel[] {
    const allShipments: ShipmentModel[] = this.entrySummary.shipments || [];
    const allInvoices: InvoiceModel[] = flatten(allShipments.map((shipment) => shipment.invoices || []));
    return flatten(allInvoices.map((invoice) => invoice.lines || []));
  }

  private getInvalidFields(fieldsToSkip?: string[]) {
    if (
      !this.formGroup.get('differentPortEntry').value &&
      this.formGroup.get('portOfEntryCode').value !== this.formGroup.get('portOfUnladingCode').value
    ) {
      this.formGroup.get('portOfUnladingCode').setValue(this.formGroup.get('portOfEntryCode').value);
    }
    const invalidFields = this.fh.validateAllFields(this.formGroup);
    for (const key of fieldsToSkip) {
      delete invalidFields[key];
    }
    if (Object.keys(invalidFields).length) {
      if (this.formGroup.get('hasSingleExportData').value) {
        delete invalidFields['foreignPortOfLadingId'];
        delete invalidFields['exportDate'];
      }
      if (this.entrySummary.transportationMode === enumTransportationMode.AIR && invalidFields['trip']) {
        delete invalidFields['trip'];
        invalidFields['flightNumber'] = 'flightNumber';
      }
      if (Object.keys(invalidFields).length) {
        return invalidFields;
      }
    }

    return {};
  }

  private createOrUpdateEntrySummary() {
    const form = this.getValidForm();
    if (this.entrySummary && this.entrySummary.id) {
      return this.entrySummaryStateService.updateEntrySummary(form).pipe(takeUntil(this.isDestroy));
    } else {
      return this.entrySummaryStateService.createEntrySummary(form).pipe(takeUntil(this.isDestroy));
    }
  }

  private getValidForm(): Partial<EntrySummaryModel> {
    const form: Partial<EntrySummaryModel> = this.formGroup.value;
    for (const field in form) {
      if (form[field] === '') {
        form[field] = null;
      }
    }
    // #13261: the mode has no form control — send it explicitly instead of relying on the
    // mutated state object being the same reference the state service spreads into the payload.
    form.transportationMode = this.entrySummary.transportationMode;

    return form;
  }

  private createErrors(): void {
    this.errors = {
      vesselName: {
        required: 'FORM_ERROR.REQUIRED',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
      },
      trip: {
        required: 'FORM_ERROR.REQUIRED',
      },
      portOfEntryCode: {
        required: 'FORM_ERROR.REQUIRED',
        notTheSameAsValidator: 'FORM_ERROR.PORT_OF_ENTRY_SAME_AS_PORT_OF_UNLADING',
      },
      portOfUnladingCode: {
        required: 'FORM_ERROR.REQUIRED',
        notTheSameAsValidator: 'FORM_ERROR.PORT_OF_UNLADING_SAME_AS_PORT_OF_ENTRY',
      },
      exportDate: {
        required: 'FORM_ERROR.REQUIRED',
      },
      arrivalDate: {
        required: 'FORM_ERROR.REQUIRED',
      },
      locationCode: {
        required: 'FORM_ERROR.REQUIRED',
      },
      stateOfDestination: {
        required: 'FORM_ERROR.REQUIRED',
      },
      type: {
        informalTypeEnteredValueTooBig: 'BACK_ERROR.INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP',
        formalTypeEnteredValueTooSmall: 'BACK_ERROR.FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP',
        informalTypeCnEnteredValueTooBig: 'BACK_ERROR.INFORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_BIG_DESKTOP',
        formalTypeCnEnteredValueTooSmall: 'BACK_ERROR.FORMAL_SHIPMENT_TYPE_ENTERED_VALUE_TOO_SMALL_DESKTOP',
        required: 'FORM_ERROR.REQUIRED',
      },
      payerUnitNumber: {
        required: 'FORM_ERROR.REQUIRED',
        maxlength: 'FORM_ERROR.MAX_LENGTH',
        minlength: 'FORM_ERROR.MIN_LENGTH',
      },
      foreignPortOfLadingId: {
        required: 'FORM_ERROR.REQUIRED',
      },
    };
  }

  private initUpdatingDatesErrors(): void {
    merge(
      this.formGroup.get('arrivalDate').valueChanges,
      this.formGroup.get('arrivalDate').statusChanges,
      this.formGroup.get('exportDate').valueChanges,
      this.formGroup.get('exportDate').statusChanges,
      this.formGroup.get('hasSingleExportData').valueChanges,
      this.formGroup.get('hasSingleExportData').statusChanges,
    )
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.updateExportMaxDate();
        this.updateArrivalMinDate(this.formGroup.get('submissionType').value);
      });
  }

  private updateExportMaxDate() {
    const exportMaxDate: string = this.formGroup.get('arrivalDate').value;
    if (exportMaxDate !== this.exportMaxDate) {
      this.exportMaxDate = exportMaxDate;
    }
  }

  private updateArrivalMinDate(submissionType: enumEntrySummarySubmissionType) {
    const arrivalMinDate: string = this.entrySummaryStateService.arrivalMinDate(
      this.entrySummary,
      this.formGroup.get('exportDate').value,
      this.formGroup.get('hasSingleExportData').value,
      submissionType,
    );
    if (arrivalMinDate !== this.arrivalMinDate) {
      this.arrivalMinDate = arrivalMinDate;
    }
  }

  private initUpdatingArrivalDateHelper(): void {
    this.formGroup
      .get('arrivalDate')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((value) => {
        if (value < DateHelper.addDaysByUTC(DateHelper.startOfTheDayByUTC(DateHelper.now), -7)) {
          this.arrivalDateHelper.next('FORM_HELPER.ARRIVAL_DATE_LESS_7_DAYS');
        } else {
          this.arrivalDateHelper.next('FORM_HELPER.SHIPMENT_ARRIVAL_DATE');
        }
      });
  }

  private updateEntrySummaryForm(isValidateAfterInit: boolean) {
    if (!this.entrySummary.id) {
      return;
    }
    // #12298: don't rebuild the form from a background reload while the user is mid-edit — it would clobber unsaved input.
    if (this.formGroup && this.formGroup.dirty) {
      return;
    }
    const {
      hasSingleExportData,
      vesselName,
      trip,
      portOfEntryCode,
      portOfUnladingCode,
      arrivalDate,
      locationCode,
      stateOfDestination,
      type,
      submissionType,
      dutiesPaymentType,
      payerUnitNumber,
    } = this.entrySummary;
    const { foreignPortOfLadingId, exportDate } = this.entrySummaryStateService.getSummaryForeignPortOfLadingData(this.entrySummary);
    const entrySummaryToUpdate = {
      trip,
      vesselName: this.isOcean ? vesselName : null,
      portOfEntryCode,
      portOfUnladingCode,
      differentPortEntry: portOfEntryCode !== portOfUnladingCode,
      arrivalDate,
      locationCode,
      hasSingleExportData,
      foreignPortOfLadingId: !hasSingleExportData ? foreignPortOfLadingId : null,
      exportDate: !hasSingleExportData && exportDate ? exportDate : null,
      stateOfDestination,
      type,
      submissionType,
      dutiesPaymentType,
      payerUnitNumber,
    };

    if (entrySummaryToUpdate.foreignPortOfLadingId) {
      this.getCurrentForeignPortOfLading(entrySummaryToUpdate.foreignPortOfLadingId);
    }

    if (entrySummaryToUpdate.portOfEntryCode) {
      this.getCurrentPorts(entrySummaryToUpdate.portOfEntryCode);
    }

    if (entrySummaryToUpdate.portOfUnladingCode && entrySummaryToUpdate.portOfEntryCode !== entrySummaryToUpdate.portOfUnladingCode) {
      this.getCurrentPorts(entrySummaryToUpdate.portOfUnladingCode);
    }

    if (entrySummaryToUpdate.locationCode) {
      this.getCurrentLocation(entrySummaryToUpdate.locationCode);
    }

    if (entrySummaryToUpdate.stateOfDestination) {
      this.getCurrentState(entrySummaryToUpdate.stateOfDestination);
    }

    const company = this.companiesService.getCompany();

    this.formGroup = this.fh.createEntryShipmentFormGroupWithData(
      entrySummaryToUpdate,
      this.isAir,
      company.authorizationAgreementAccepted && !!company.payerUnitNumber,
    );

    this.initUpdatingDatesErrors();
    this.initValidatorChanges();
    this.initUpdatingArrivalDateHelper();
    this.updateTransportationModeValidation();

    this.formGroup.enable({ onlySelf: false });
    this.checkAuthorizationAgreement();

    if ([undefined, null].includes(this.entrySummary.sentToProviderDate) && enumEntrySummaryState.NOT_SENT === this.entrySummary.state) {
      this.formGroup.get('submissionType').enable();
    } else {
      this.formGroup.get('submissionType').disable();
    }

    if (isValidateAfterInit) {
      this.fh.markAsTouched(this.formGroup);
    }

    if (
      this.entrySummary.submissionType === enumEntrySummarySubmissionType.BROKER_ASSISTED &&
      this.entrySummary.bondRequest &&
      this.entrySummary.bondRequest.id &&
      this.isShowSubmissionType
    ) {
      this.formGroup
        .get('submissionType')
        .valueChanges.pipe(takeUntil(this.isDestroy), distinctUntilChanged())
        .subscribe((value) => {
          if (value === enumEntrySummarySubmissionType.MANUAL) {
            this.showChangeEntrySubmissionTypeConfirmModal = true;
          }
        });
    }

    if (this.entrySummary.type === enumEntrySummaryType.FORMAL && this.entrySummary.bondRequest && this.entrySummary.bondRequest.id) {
      this.formGroup
        .get('type')
        .valueChanges.pipe(takeUntil(this.isDestroy), distinctUntilChanged())
        .subscribe((value) => {
          if (value === enumEntrySummaryType.INFORMAL) {
            this.showChangeEntryTypeConfirmModal = true;
          }
        });
    }

    this.formGroup
      .get('submissionType')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((value: enumEntrySummarySubmissionType) => {
        if (value === enumEntrySummarySubmissionType.MANUAL) {
          const arrivalDateValue = this.formGroup.get('arrivalDate').value;

          if (arrivalDateValue < DateHelper.addDaysByUTC(DateHelper.startOfTheDayByUTC(DateHelper.now), -7)) {
            this.formGroup.get('arrivalDate').patchValue(null);
          }
        }

        this.updateArrivalMinDate(value);
      });
  }

  private initValidatorChanges() {
    this.formGroup
      .get('differentPortEntry')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((value) => {
        const portOfEntryCode = this.formGroup.get('portOfEntryCode');
        const portOfUnladingCode = this.formGroup.get('portOfUnladingCode');

        portOfEntryCode.clearValidators();
        portOfUnladingCode.clearValidators();
        if (value) {
          portOfEntryCode.setValidators([Validators.required, notTheSameAsValidator('portOfUnladingCode', 'notTheSameAsValidator')]);
          portOfUnladingCode.setValidators([Validators.required, notTheSameAsValidator('portOfEntryCode', 'notTheSameAsValidator')]);
        } else {
          portOfUnladingCode.clearValidators();
          portOfEntryCode.setValidators(Validators.required);
        }
        if (value && portOfUnladingCode.value && portOfUnladingCode.value === portOfEntryCode.value) {
          portOfUnladingCode.patchValue(null);
        } else if (!value && portOfUnladingCode.value !== portOfEntryCode.value) {
          portOfUnladingCode.patchValue(portOfEntryCode.value);
        }
        portOfEntryCode.updateValueAndValidity();
        portOfUnladingCode.updateValueAndValidity();
      });

    this.formGroup
      .get('dutiesPaymentType')
      .valueChanges.pipe(takeUntil(this.isDestroy))
      .subscribe((dutiesPaymentType) => {
        if (dutiesPaymentType === enumEntrySummaryDutiesPaymentType.INSTANT) {
          this.formGroup.get('payerUnitNumber').clearValidators();
          this.formGroup.get('payerUnitNumber').patchValue(null);
        } else {
          this.formGroup.get('payerUnitNumber').setValidators([Validators.required, Validators.maxLength(6), Validators.minLength(6)]);
          if (this.payerUnitNumber && !this.formGroup.get('payerUnitNumber').value) {
            this.formGroup.get('payerUnitNumber').patchValue(this.payerUnitNumber);
          }
        }
      });
  }

  private getCurrentForeignPortOfLading(id: number) {
    this.codeRepository
      .getForeignPort(id)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((port) => {
        this.initialForeignPorts = [port];
      });
  }

  private getCurrentPorts(code: string) {
    if (this.initialPorts.findIndex((port) => port.code === code) === -1) {
      this.codeRepository
        .getDomesticPort(code)
        .pipe(takeUntil(this.isDestroy))
        .subscribe((port) => {
          this.initialPorts = UtilsHelper.concat<PortModel>(this.initialPorts, port);
        });
    } else {
      this.initialPorts = cloneDeep(this.initialPorts); // need update the link for array in case if port location was founded early
    }
  }

  private getCurrentLocation(code: string) {
    this.codeRepository
      .getLocation(code)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((location) => {
        this.initialLocations = [location];
      });
  }

  private getCurrentState(code: string) {
    this.codeRepository
      .getState(code)
      .pipe(takeUntil(this.isDestroy))
      .subscribe((states) => {
        this.initialStates = [states];
      });
  }

  private createOrUpdateShipment(entrySummary: EntrySummaryModel): Observable<Partial<EntrySummaryModel>> {
    const { foreignPortOfLadingId: prevForeignPortOfLadingId, exportDate: prevExportDate } =
      this.entrySummaryStateService.getSummaryForeignPortOfLadingData(this.entrySummary);
    const { hasSingleExportData, foreignPortOfLadingId, exportDate } = this.formGroup.value;

    const differentPortEntry = entrySummary.portOfEntryCode !== entrySummary.portOfUnladingCode;
    const hasShipmentsWithIT = entrySummary.shipments.some((shipment) => !!(shipment.itDate || shipment.itNumber));

    const isChangedForeignPort = foreignPortOfLadingId !== prevForeignPortOfLadingId;
    const isChangedExportDate = exportDate !== prevExportDate;

    const isNeedUpdateShipments =
      (!hasSingleExportData && (isChangedForeignPort || isChangedExportDate)) || (!differentPortEntry && hasShipmentsWithIT);

    if (isNeedUpdateShipments) {
      if (entrySummary.shipments.length === 0) {
        return this.createShipment({ foreignPortOfLadingId, exportDate }, entrySummary);
      } else {
        const shipmentsToUpdate = entrySummary.shipments
          .filter(
            (shipment) =>
              shipment.foreignPortOfLadingId !== foreignPortOfLadingId ||
              shipment.exportDate !== exportDate ||
              (!differentPortEntry && (shipment.itDate || shipment.itNumber)),
          )
          .map((shipment) => {
            if (!differentPortEntry) {
              shipment.itDate = null;
              shipment.itNumber = null;
            }
            return shipment;
          });
        return this.updateShipments(shipmentsToUpdate, { foreignPortOfLadingId, exportDate }, entrySummary);
      }
    }

    return of({ ...entrySummary, hasSingleExportData });
  }

  /**
   * #13261: House bill is hidden for Rail and Truck but lives on the shipment, so a mode change
   * (PUT entry-summary) leaves it in the data and every later BL save re-sends it. Null it out on
   * the shipments that still carry a value. Direction-independent and idempotent — also cleans
   * legacy stale values. Only the "details" section can change the mode.
   */
  private clearHouseBillIfHidden(entrySummary: EntrySummaryModel): Observable<Partial<EntrySummaryModel>> {
    if (!this.isMainInfo) {
      return of(entrySummary);
    }
    const shipmentsWithHouseBill: ShipmentModel[] = EsTransportationModeHelper.getShipmentsWithStaleHouseBill(entrySummary);
    if (shipmentsWithHouseBill.length === 0) {
      return of(entrySummary);
    }
    return this.updateShipments(shipmentsWithHouseBill, EsTransportationModeHelper.EMPTY_HOUSE_BILL, entrySummary);
  }

  private createShipment(data, entrySummary: EntrySummaryModel): Observable<EntrySummaryModel> {
    return this.entrySummaryStateService.createShipment(data).pipe(
      switchMap((shipment) => {
        entrySummary.shipments = [shipment];
        return of(entrySummary);
      }),
    );
  }

  private updateShipments(
    shipmentsToUpdate: ShipmentModel[],
    fieldsToUpdate: Partial<ShipmentModel>,
    entrySummary: EntrySummaryModel,
  ): Observable<EntrySummaryModel> {
    return forkJoin(
      shipmentsToUpdate.map((shipment) => {
        return this.entrySummaryStateService.updateShipment(shipment, fieldsToUpdate);
      }),
    ).pipe(
      switchMap((updatedShipments: ShipmentModel[]) => {
        entrySummary.shipments = this.getUpdatedShipmentsForEntrySummary(updatedShipments, entrySummary);
        return of(entrySummary);
      }),
    );
  }

  private getUpdatedShipmentsForEntrySummary(updatedShipments: ShipmentModel[], entrySummary: EntrySummaryModel) {
    const updatedShipmentsId = updatedShipments.filter((item) => item).map((item) => item.id);
    return entrySummary.shipments.map((item) => {
      if (updatedShipmentsId.includes(item.id)) {
        return updatedShipments.find((updatedShipment) => item.id === updatedShipment.id);
      }
      return item;
    });
  }

  private openModal(data) {
    this.missingFieldModal.openCustomModal(data);
  }

  private checkAuthorizationAgreement() {
    if (!this.companiesService.getCompany().authorizationAgreementAccepted || !this.companiesService.getCompany().payerUnitNumber) {
      this.showUpdateCompanyAchDisclaimer = true;
      if (this.entrySummary?.dutiesPaymentType === enumEntrySummaryDutiesPaymentType.INSTANT) {
        this.formGroup.get('dutiesPaymentType').disable();
        this.formGroup.get('payerUnitNumber').disable();
        return;
      }
    } else {
      this.showUpdateCompanyAchDisclaimer = false;
    }

    this.formGroup.get('dutiesPaymentType').enable();
    this.formGroup.get('payerUnitNumber').enable();
  }

  private findShipmentError(field: string): string {
    const errors: ValidationMessageModel[] = this.entrySummary?.shipments?.[0]?.validationMessages || [];

    return errors.find((item: ValidationMessageModel) => item.text === field)?.text;
  }
}
